import { REPORT_TYPES } from "./Reports";

export interface BlueprintAnnotatedApi {
  workflow_config_uuid?: string;
  name?: string;
  is_active: boolean;
  is_root: boolean;
  copied_from: number;
  copied_from_uuid?: string;
  external_id?: string;
  item_types: BlueprintItemTypeApi[];
  sources: BlueprintItemSourceApi[];
  behaviors: BehaviorInfoApi[];
  is_template: boolean;
  lookback: number;
  sla_hours: number;
  workflow_steps?: unknown;
  keyword_groups?: unknown;
}

export interface BlueprintItemTypeApi {
  external_id?: string;
  name: string;
  item_type_uuid: string;
  is_active: boolean;
}

export interface BlueprintItemSourceApi {
  external_id?: string;
  name: string;
  item_source_uuid: string;
  is_active: boolean;
  modified_by: string;
}

export interface BehaviorInfoApi {
  external_id?: string;
  name: string;
  media: string;
  is_active: boolean;
}

export interface BlueprintInfo {
  workflow_config_uuid: string;
  name?: string;
  lookback: number;
  sla_hours: number;
  sources: BlueprintItemSourceInfoMinimal[];
  item_types: BlueprintItemTypeInfoMinimal[];
  behaviors: BehaviorInfoMinimal[];
  external_id?: string;
  copied_from_uuid?: string;
  is_template?: boolean;
  turn_around?: TURN_AROUND_CALCULATIONS;
  color?: string;
  report_type?: REPORT_TYPES;
  description?: string;
}

export interface BlueprintMinimalInfo {
  blueprint_uuid: string;
  name: string;
  report_type: REPORT_TYPES;
}

export interface CheckTypeInfo {
  blueprint_uuid: string;
  name: string;
  report_type?: string;
  workflow_name?: string;
  workflow_version?: number;
  sources: string[];
  behaviors: string[];
  item_types?: string[];
  sla_hours: number;
  turn_around?: string;
  lookback: number;
  color?: string;
  description?: string;
}

export interface BehaviorKitMinimalInfo {
  behavior_kit_uuid: string;
  name: string;
  expanded?: boolean;
}

export interface BlueprintListInputApi {
  filter?: string;
  pageSize: number;
  pageNumber: number;
}

export interface BlueprintItemTypeInfoMinimal {
  blueprintItemType_uuid: string;
  item_type_uuid: string;
  name: string;
  isActive?: boolean;
  is_fixed?: boolean;
}

export interface BlueprintItemSourceInfoMinimal {
  blueprintItemSource_uuid: string;
  item_source_uuid: string;
  name: string;
  isActive?: boolean;
  is_fixed?: boolean;
}

export interface BehaviorInfoMinimal {
  behavior_uuid: string;
  name: string;
  is_active?: boolean;
  media?: string;
  external_id?: string; // Needed to update behavior
}

export interface BlueprintCopyInput {
  blueprint_uuid: string;
  company_uuid: string;
  modified_by: string; // TODO: remove once actor_uuid changes are only
  include_sub_companies: boolean;
}

export interface BlueprintAssignInput {
  blueprint_uuid: string;
  company_uuid: string;
}

export interface BlueprintAssignedApi {
  blueprint_uuid: string;
  company_blueprint_uuid: string;
}

export interface BlueprintWorkflowStepsBase {
  blueprint_id: number;
  workflow_config_uuid: string;
  workflow_step_config_uuid: string;
  shouldPass: boolean;
  modified_by?: string;
  name: string;
  is_active: boolean;
  is_fixed: boolean;
}

export interface BlueprintEx2 {
  external_id: string;
  modified_by: string;
  workflow_config_uuid: string;
  sla_hours: number;
  lookback: number;
  name: string;
  is_active: boolean;
  is_template: boolean;
  report_type?: REPORT_TYPES;
  is_root: boolean;
  copied_from: number;
  item_types: BlueprintItemTypeApi[];
  item_sources: BlueprintItemSourceApi[];
  behaviors: string[];
  lookback_months: number; // Updated computed lookback
  workflow_steps: WorkflowSteps[] | unknown;
  keyword_groups: KeywordGroupExternal[] | unknown;
  /**
   * Frozen on the workflow computed blueprint when the report is created.
   * Later company setting changes do not update this value.
   */
  lens_topics_enabled?: boolean;
  color?: string;
}
export interface WorkflowSteps {
  external_id: string;
  created_at: string;
  modified_at: string;
  workflow_config_uuid: string;
  workflow_step_config_uuid: string;
  shouldPass: boolean;
  modified_by: string;
  name: string;
  is_active: boolean;
  is_fixed: boolean;
}
export interface CreateBlueprintBehaviors {
  name: string;
  media?: string;
}

interface Dict<T> {
  [key: string]: T;
}
export interface CreateBlueprintSources {
  name: string;
  item_source_uuid: string;
  is_fixed?: boolean;
  options?: Dict<boolean>;
}

export interface CreateBlueprintItemTypes {
  name: string;
  item_type_uuid: string;
  external_id?: string;
  is_active?: boolean;
  is_fixed?: boolean;
}

export interface CreateBlueprintTemplate {
  sla_hours: number;
  lookback: number;
  turn_around?: TURN_AROUND_CALCULATIONS;
  blueprint_root_uuid: string;
  name: string;
  description?: string;
  blueprint_behavior: CreateBlueprintBehaviors[];
  blueprint_sources: CreateBlueprintSources[];
  blueprint_types: CreateBlueprintItemTypes[];
  color?: string;
  report_type: string;
  steps_to_skip?: string;
}

export interface KeywordGroupInputCreate {
  behavior_kit_id?: string;
  blueprint_id?: string;
  name: string;
  include_exact: string[];
  include_fuzzy: string[];
  exclude_exact: string[];
  exclude_fuzzy: string[];
}

export interface KeywordGroupInputUpdate {
  external_id: string;
  name: string;
  include_exact: string[];
  include_fuzzy: string[];
  exclude_exact: string[];
  exclude_fuzzy: string[];
}

export type KeywordGroupExternal = KeywordGroupInputCreate & {
  external_id: string;
};

export interface KeywordModalState {
  includeExact: string[];
  includeFuzzy: string[];
  excludeExact: string[];
  excludeFuzzy: string[];
  name: string;
  error?: { [key: string]: boolean };
}

export interface LensTopicsInput {
  behavior_kit_id: string;
  name: string;
  seeds: string[];
  topics: string[];
}

export interface LensTopicsUpdateInput {
  external_id: string;
  behavior_kit_id: string;
  name: string;
  seeds: string[];
  topics: string[];
}

export type LensTopicsExternal = {
  external_id: string;
  blueprint_id?: string | null;
  behavior_kit_id?: string | null;
  name: string;
  seeds: string[];
  topics: string[];
};

export enum TURN_AROUND_CALCULATIONS {
  Hour = "HOURS",
  Days = "BUSINESS_DAYS",
  SingleBusinessDay = "SINGLE_BUSINESS_DAY",
  ThreeSixty = "THREE_SIXTY",
}

export enum READABLE_TURN_AROUND {
  Hour = "Hour Calculation",
  Days = "Business Day Calculation",
  SingleBusinessDay = "Business Day Calculation (-1 min)",
  ThreeSixty = "360 Report Standard SLA",
}

export enum BlueprintItemTypes {
  ScrapeInstagramPosts = "scrape_instagram_posts",
  ScrapeInstagramReels = "scrape_instagram_reels",
}

export interface NameOnly {
  external_id: string;
  name: string;
}
